import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteOwnAccount } from "@/features/user-profile/delete-account";
import { authKeys } from "@/services/auth/auth.options";
import { authService } from "@/services/auth/auth.service";
import { authStore } from "@/stores/auth-store";
import type { User as AuthUser } from "@/types/auth.types";
import { organizationKeys } from "@/services/organization/organization.options";
import { userKeys } from "./user.options";
import { userService } from "./user.service";
import type {
	CreatePlatformUserInfo,
	GetUsersParams,
	ResetPasswordRequest,
	UpdateUserInfo,
	UpdateUserPlatformRole,
	UpdateUserStatus,
	User,
	UserMfaInfo,
} from "./user.types";

/**
 * Merge defined UpdateUserInfo fields into the auth-store user when editing self.
 * Profile drawer reads from auth store, not userKeys caches.
 */
function patchAuthUserIfSelf(userId: string, data: UpdateUserInfo): void {
	const { user, setUser } = authStore.getState();
	if (!user || user.id !== userId) return;

	const patch: Partial<AuthUser> = {};
	if (data.firstName !== undefined) {
		patch.firstName = data.firstName ?? "";
	}
	if (data.lastName !== undefined) {
		patch.lastName = data.lastName ?? "";
	}
	if (data.displayName !== undefined) {
		patch.displayName = data.displayName ?? "";
	}
	if (data.phone !== undefined) {
		patch.phone = data.phone;
	}

	if (Object.keys(patch).length === 0) return;
	setUser({ ...user, ...patch });
}

function patchSignedInHasAvatar(userId: string, hasAvatar: boolean): void {
	const { user, setUser } = authStore.getState();
	if (!user || user.id !== userId) return;
	setUser({ ...user, hasAvatar });
}

// ==================== USER MUTATIONS ====================

/**
 * Create platform user (stubbed service)
 */
export const useCreatePlatformUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreatePlatformUserInfo) =>
			userService.createPlatformUser(data),
		onMutate: async (newUser) => {
			await queryClient.cancelQueries({ queryKey: userKeys.users() });
			const previous = queryClient.getQueriesData({
				queryKey: userKeys.users(),
			});
			queryClient.setQueriesData({ queryKey: userKeys.usersList }, (old) => {
				if (!old || typeof old !== "object") return old;
				const oldData = old as { data?: unknown[] };
				return {
					...oldData,
					data: [
						...(oldData.data || []),
						{
							id: `temp-${Date.now()}`,
							...newUser,
						},
					],
				};
			});
			return { previous };
		},
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: userKeys.users() });
			queryClient.invalidateQueries({
				queryKey: organizationKeys.globalRoles(),
			});
			if ((result as any)?.affectedId) {
				queryClient.invalidateQueries({
					queryKey: userKeys.usersDetail((result as any).affectedId),
				});
			}
			return result as any;
		},
		onError: (_error, _newUser, context) => {
			if (context?.previous) {
				context.previous.forEach(([queryKey, data]: any) => {
					queryClient.setQueryData(queryKey, data);
				});
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: userKeys.users() });
		},
	});
};

/**
 * Update user information with optimistic updates
 */
export const useUpdateUserInfo = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: UpdateUserInfo }) =>
			userService.updateUserInfo(userId, data),
		onMutate: async ({ userId, data }) => {
			// Cancel any outgoing refetches for this user
			await queryClient.cancelQueries({
				queryKey: userKeys.usersDetail(userId),
			});

			// Snapshot the previous value
			const previousUser = queryClient.getQueryData<User>(
				userKeys.usersDetail(userId),
			);

			// Optimistically update the user detail
			if (previousUser) {
				queryClient.setQueryData(userKeys.usersDetail(userId), {
					...previousUser,
					...data,
					updatedAt: new Date().toISOString(),
				});
			}

			// Also update the user in any list queries that might contain this user
			const previousLists = queryClient.getQueriesData({
				queryKey: userKeys.users(),
			});

			previousLists.forEach(([queryKey, oldData]) => {
				if (!oldData || typeof oldData !== "object") return;
				const listData = oldData as { data?: User[] };
				if (listData.data) {
					queryClient.setQueryData(queryKey, {
						...listData,
						data: listData.data.map((user) =>
							user.id === userId
								? { ...user, ...data, updatedAt: new Date().toISOString() }
								: user,
						),
					});
				}
			});

			const previousAuthUser = authStore.getState().user;
			const previousAuthUserSnapshot =
				previousAuthUser?.id === userId ? previousAuthUser : null;
			patchAuthUserIfSelf(userId, data);

			// Return a context object with the snapshotted values
			return {
				previousUser,
				previousLists,
				previousAuthUser: previousAuthUserSnapshot,
			};
		},
		onError: (_error, { userId }, context) => {
			// If the mutation fails, roll back to the previous values
			if (context?.previousUser) {
				queryClient.setQueryData(
					userKeys.usersDetail(userId),
					context.previousUser,
				);
			}

			// Restore previous list data
			if (context?.previousLists) {
				context.previousLists.forEach(([queryKey, data]: any) => {
					queryClient.setQueryData(queryKey, data);
				});
			}

			if (context?.previousAuthUser) {
				authStore.getState().setUser(context.previousAuthUser);
			}

			console.error("Failed to update user info:", _error);
		},
		onSuccess: (_result, variables) => {
			const { userId, data } = variables;

			// Invalidate the specific user
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});

			// Invalidate users list queries (name might have changed)
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});

			// Invalidate user permissions (might include display name)
			queryClient.invalidateQueries({
				queryKey: userKeys.usersPermissions(userId),
			});

			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});

			queryClient.invalidateQueries({
				queryKey: authKeys.userInfo(),
			});
			patchAuthUserIfSelf(userId, data);
		},
		onSettled: (_data, _error, { userId }) => {
			// Always refetch after error or success to ensure data consistency
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});
		},
	});
};

/**
 * Update user status
 */
export const useUpdateUserStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: string;
			data: UpdateUserStatus;
		}) => userService.updateUserStatus(userId, data),
		onSuccess: (_result, variables) => {
			const { userId } = variables;

			// Invalidate the specific user
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});

			// Invalidate users list queries (status might affect filtering)
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});

			// Invalidate user permissions
			queryClient.invalidateQueries({
				queryKey: userKeys.usersPermissions(userId),
			});

			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});

			queryClient.invalidateQueries({
				queryKey: authKeys.checkActionRoot(),
				refetchType: "all",
			});
			queryClient.invalidateQueries({
				queryKey: authKeys.checkActionsRoot(),
				refetchType: "all",
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.globalRoles(),
			});
		},
		onError: (error) => {
			console.error("Failed to update user status:", error);
		},
	});
};

/**
 * Update user platform role
 */
export const useUpdateUserPlatformRole = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: string;
			data: UpdateUserPlatformRole;
		}) => userService.updateUserPlatformRole(userId, data),
		onSuccess: (_result, variables) => {
			const { userId } = variables;

			// Invalidate the specific user
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});

			// Invalidate users list queries (role might affect filtering)
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});

			// Invalidate user permissions (role change affects permissions)
			queryClient.invalidateQueries({
				queryKey: userKeys.usersPermissions(userId),
			});

			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});
			queryClient.invalidateQueries({
				queryKey: organizationKeys.globalRoles(),
			});
		},
		onError: (error) => {
			console.error("Failed to update user platform role:", error);
		},
	});
};

/**
 * Export users to CSV
 */
export const useExportUsers = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: GetUsersParams;
		}) => {
			const blob = await userService.exportUsers(startIndex, count, params);
			return blob;
		},
		onError: (error) => {
			console.error("Export failed:", error);
		},
	});
};

// ==================== OPTIMISTIC UPDATES ====================

/**
 * Update user info with optimistic updates
 */
export const useOptimisticUpdateUserInfo = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: UpdateUserInfo }) =>
			userService.updateUserInfo(userId, data),
		onMutate: async ({ userId, data }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: userKeys.usersDetail(userId),
			});

			// Snapshot the previous value
			const previousUser = queryClient.getQueryData<User>(
				userKeys.usersDetail(userId),
			);

			// Optimistically update the user
			if (previousUser) {
				queryClient.setQueryData(userKeys.usersDetail(userId), {
					...previousUser,
					...data,
					updatedAt: new Date().toISOString(),
				});
			}

			// Return a context object with the snapshotted value
			return { previousUser };
		},
		onError: (_error, { userId }, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousUser) {
				queryClient.setQueryData(
					userKeys.usersDetail(userId),
					context.previousUser,
				);
			}
		},
		onSettled: (_data, _error, { userId }) => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});
		},
	});
};

/**
 * Update user status with optimistic updates
 */
export const useOptimisticUpdateUserStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: string;
			data: UpdateUserStatus;
		}) => userService.updateUserStatus(userId, data),
		onMutate: async ({ userId, data }) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: userKeys.usersDetail(userId),
			});

			// Snapshot the previous value
			const previousUser = queryClient.getQueryData<User>(
				userKeys.usersDetail(userId),
			);

			// Optimistically update the user status
			if (previousUser) {
				queryClient.setQueryData(userKeys.usersDetail(userId), {
					...previousUser,
					status: {
						...previousUser.status,
						name: data.status,
					},
					updatedAt: new Date().toISOString(),
				});
			}

			// Return a context object with the snapshotted value
			return { previousUser };
		},
		onError: (_error, { userId }, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousUser) {
				queryClient.setQueryData(
					userKeys.usersDetail(userId),
					context.previousUser,
				);
			}
		},
		onSettled: (_data, _error, { userId }) => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});
		},
	});
};

// ==================== BATCH OPERATIONS ====================

/**
 * Batch update multiple users
 */
export const useBatchUpdateUsers = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			updates,
		}: {
			updates: Array<{ userId: string; data: UpdateUserInfo }>;
		}) => {
			// Execute all updates in parallel
			const results = await Promise.allSettled(
				updates.map(({ userId, data }) =>
					userService.updateUserInfo(userId, data),
				),
			);

			// Return results with success/failure information
			return results.map((result, index) => ({
				userId: updates[index].userId,
				success: result.status === "fulfilled",
				error: result.status === "rejected" ? result.reason : undefined,
			}));
		},
		onSuccess: () => {
			// Invalidate all user queries
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});
		},
		onError: (error) => {
			console.error("Failed to batch update users:", error);
		},
	});
};

/**
 * Batch update user statuses
 */
export const useBatchUpdateUserStatuses = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			updates,
		}: {
			updates: Array<{ userId: string; data: UpdateUserStatus }>;
		}) => {
			// Execute all updates in parallel
			const results = await Promise.allSettled(
				updates.map(({ userId, data }) =>
					userService.updateUserStatus(userId, data),
				),
			);

			// Return results with success/failure information
			return results.map((result, index) => ({
				userId: updates[index].userId,
				success: result.status === "fulfilled",
				error: result.status === "rejected" ? result.reason : undefined,
			}));
		},
		onSuccess: () => {
			// Invalidate all user queries
			queryClient.invalidateQueries({
				queryKey: userKeys.users(),
			});
		},
		onError: (error) => {
			console.error("Failed to batch update user statuses:", error);
		},
	});
};

// ==================== UTILITY MUTATIONS ====================

/**
 * Invalidate all user-related queries
 */
export const useInvalidateUserQueries = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (userId?: string) => {
			if (userId) {
				// Invalidate queries for a specific user
				await Promise.all([
					queryClient.invalidateQueries({
						queryKey: userKeys.usersDetail(userId),
					}),
					queryClient.invalidateQueries({
						queryKey: userKeys.usersPermissions(userId),
					}),
				]);
			} else {
				// Invalidate all user queries
				await queryClient.invalidateQueries({
					queryKey: userKeys.all,
				});
			}
		},
	});
};

/**
 * Prefetch user data
 */
export const usePrefetchUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (userId: string) => {
			// Prefetch user and its related data
			await Promise.all([
				queryClient.prefetchQuery({
					queryKey: userKeys.usersDetail(userId),
					queryFn: () => userService.getUserById(userId),
					staleTime: 5 * 60 * 1000,
				}),
				queryClient.prefetchQuery({
					queryKey: userKeys.usersPermissions(userId),
					queryFn: () => userService.getUserPermissions(userId),
					staleTime: 10 * 60 * 1000,
				}),
			]);
		},
	});
};

// ==================== AVATAR MUTATIONS ====================

/**
 * Upload a user's avatar
 * POST /users/{userId}/avatar
 * Accepts multipart/form-data with 'file' field
 */
export const useUploadUserAvatar = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, file }: { userId: string; file: File | Blob }) =>
			userService.uploadUserAvatar(userId, file),
		onSuccess: async (_result, { userId }) => {
			try {
				const userInfo = await authService.getUserInfo();
				patchSignedInHasAvatar(userId, userInfo.hasAvatar === true);
				queryClient.setQueryData(authKeys.userInfo(), userInfo);
			} catch (error) {
				console.error("Failed to refresh avatar flag:", error);
			}
			await queryClient.invalidateQueries({
				queryKey: userKeys.avatar(userId),
			});
			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});
		},
		onError: (error) => {
			console.error("Failed to upload avatar:", error);
		},
	});
};

/**
 * Delete the signed-in user's own account.
 * DELETE /users/{userId}
 * 401 and 404 resolve as "ended" so the global mutation toast does not fire.
 */
export const useDeleteUser = () => {
	return useMutation({
		mutationFn: (userId: string) =>
			deleteOwnAccount(() => userService.deleteUser(userId)),
	});
};

export const useDeleteUserAvatar = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (userId: string) => userService.deleteUserAvatar(userId),
		onSuccess: (_result, userId) => {
			patchSignedInHasAvatar(userId, false);
			queryClient.setQueryData(authKeys.userInfo(), (current) => {
				if (!current || typeof current !== "object") return current;
				return { ...current, hasAvatar: false };
			});
			queryClient.removeQueries({
				queryKey: userKeys.avatar(userId),
			});
			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});
		},
		onError: (error) => {
			console.error("Failed to delete avatar:", error);
		},
	});
};

// ==================== PASSWORD RESET MUTATIONS ====================

/**
 * Request password reset for a user
 * PUT /users/{userId}/request-password-reset
 * No request body - triggers email to user
 */
export const useRequestPasswordReset = () => {
	return useMutation({
		mutationFn: (userId: string) => userService.requestPasswordReset(userId),
		onError: (error) => {
			console.error("Failed to request password reset:", error);
		},
	});
};

/**
 * Force password reset for a user (admin action)
 * PUT /users/{userId}/force-password-reset
 * No request body - forces user to reset password on next login
 */
export const useForcePasswordReset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (userId: string) => userService.forcePasswordReset(userId),
		onSuccess: (_result, userId) => {
			// Invalidate user detail to reflect password reset state
			queryClient.invalidateQueries({
				queryKey: userKeys.usersDetail(userId),
			});
			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});
		},
		onError: (error) => {
			console.error("Failed to force password reset:", error);
		},
	});
};

/**
 * Reset password with token
 * PUT /users/{userId}/reset-password
 * Used when user clicks reset link from email
 */
export const useResetPassword = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: string;
			data: ResetPasswordRequest;
		}) => userService.resetPassword(userId, data),
		onSuccess: (_result, { userId }) => {
			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});
		},
		onError: (error) => {
			console.error("Failed to reset password:", error);
		},
	});
};

/**
 * Send MFA verification code to a phone number
 * POST /users/{userId}/mfa/verification-code
 */
export const useSendMfaVerificationCode = () => {
	return useMutation({
		mutationFn: ({
			userId,
			phoneNumber,
		}: {
			userId: string;
			phoneNumber: string;
		}) => userService.sendMfaVerificationCode(userId, phoneNumber),
		onError: (error) => {
			console.error("Failed to send MFA verification code:", error);
		},
	});
};

/**
 * Update user MFA (submit phone + verification code)
 * PUT /users/{userId}/mfa
 */
export const useUpdateMfa = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: UserMfaInfo }) =>
			userService.updateMfa(userId, data),
		onSuccess: (_result, { userId }) => {
			queryClient.invalidateQueries({
				queryKey: userKeys.usersHistory(userId),
			});
		},
		onError: (error) => {
			console.error("Failed to update MFA:", error);
		},
	});
};

// ==================== GROUPED EXPORTS ====================

/**
 * All user mutations grouped by category
 */
export const userMutations = {
	// Core user mutations
	updateInfo: useUpdateUserInfo,
	updateStatus: useUpdateUserStatus,
	updatePlatformRole: useUpdateUserPlatformRole,
	exportUsers: useExportUsers,

	// Avatar mutations
	uploadAvatar: useUploadUserAvatar,
	deleteAvatar: useDeleteUserAvatar,

	// Password reset mutations
	requestPasswordReset: useRequestPasswordReset,
	forcePasswordReset: useForcePasswordReset,

	// MFA mutations
	sendMfaVerificationCode: useSendMfaVerificationCode,
	updateMfa: useUpdateMfa,
	resetPassword: useResetPassword,

	// Optimistic updates
	optimisticUpdateInfo: useOptimisticUpdateUserInfo,
	optimisticUpdateStatus: useOptimisticUpdateUserStatus,

	// Batch operations
	batchUpdate: useBatchUpdateUsers,
	batchUpdateStatuses: useBatchUpdateUserStatuses,

	// Utility mutations
	utils: {
		invalidateQueries: useInvalidateUserQueries,
		prefetch: usePrefetchUser,
	},
} as const;
